import { useEffect } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { InvoiceForm } from "@/components/sales-invoices/InvoiceForm";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import { useCompany } from "@/lib/api/companies.queries";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute("/companies/$companyId/facturas/new")({
  component: NewInvoicePage,
});

function NewInvoicePage() {
  return (
    <RequireAuth>
      <AppShell>
        <NewInvoiceContent />
      </AppShell>
    </RequireAuth>
  );
}

function NewInvoiceContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const companyQuery = useCompany(companyId);

  if (companyQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (companyQuery.isError || !companyQuery.data) {
    return (
      <ErrorState
        title={t("invoices.list.error_title")}
        description={t("invoices.list.error_body")}
        onRetry={() => void companyQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="factura_venta" />
      <div>
        <Link
          to="/companies/$companyId/facturas"
          params={{ companyId }}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("invoices.new.back")}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">{t("invoices.new.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("invoices.new.subtitle")}</p>
      </div>
      <InvoiceForm companyId={companyId} />
    </div>
  );
}
